import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { ActivatedRoute, Router } from '@angular/router';
import { FiltersService } from 'src/app/core/services/filters.service';
import { TvdProgramsStore } from '../../stores/tvd-programs.store';
import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';
import { UIPreferencesService } from 'src/app/core/services/ui-preferences.service';
import { TvdByProgramData, TvdImpactCategory } from '../../types/tvd-types';

/**
 * Container component for displaying comprehensive TVD program summary and the details table.
 * Responsible for aggregating program-level metrics for summary display using the query parameters to identify the selected program,
 * and passing program name to the TvdProgramDetailsTableComponent for detailed display.
 * This component focuses on program-level aggregation and summary, while the child table component handles detailed data display and interactions.
 */
@Component({
    selector: 'app-tvd-program-details-page',
    templateUrl: './tvd-program-details-page.component.html',
    styleUrls: ['./tvd-program-details-page.component.scss'],
    standalone: false
})
export class TvdProgramDetailsPageComponent implements OnInit, OnDestroy {
  private _programTvdData: TvdByProgramData | undefined = undefined;

  // Subscriptions
  private _filtersSub?: Subscription;
  private _displayedCurrencyCodeSub?: Subscription;
  private _displayInRoundedNumberSub?: Subscription;
  private _currencyUomSub?: Subscription;
  private _programsCacheInvalidatedSub?: Subscription;

  // UI state
  filters: ContractModel | undefined;
  displayInRoundedNumber: boolean = false;
  currencyCode: string = 'USD';
  isLoading: boolean = true;

  // Program details for UI display
  totalValueDelivered: number = 0;
  programName: string = '';
  totalLocations: number = 0;
  totalProducts: number = 0;
  averageTvdPerProduct: number = 0;
  impactCategories: string[] = [];

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _filtersService: FiltersService,
    private _uiPreferencesService: UIPreferencesService,
    private _currencyUomService: CurrencyUomService,
    private _tvdProgramsStore: TvdProgramsStore,
  ) {}

  ngOnInit() {
    this._filtersSub = this._filtersService.selectedFiltersObservable.subscribe(
      (f) => (this.filters = f),
    );

    if (this.filters === undefined) {
      alert('Please apply filters before navigating to Total Value Delivery');
      this._router.navigate(['/filter']);
    }

    this._displayInRoundedNumberSub =
      this._uiPreferencesService.displayRoundedValuesObservable.subscribe(
        (f) => (this.displayInRoundedNumber = f),
      );

    this._displayedCurrencyCodeSub =
      this._currencyUomService.displayedCurrency$.subscribe(() => {
        this.currencyCode = this._currencyUomService.getDisplayedCurrency();
      });

    // Subscribe to cache invalidation triggers from the store
    this._programsCacheInvalidatedSub =
      this._tvdProgramsStore.cacheInvalidated$.subscribe(async () => {
        this._currencyUomService.setCurrencyLoading(true);
        try {
          if (this.programName) {
            await this.calculateProgramSummary(this.programName);
          }
        } finally {
          this._currencyUomService.setCurrencyLoading(false);
        }
      });

    // Initial load based on query params
    this._activatedRoute.queryParamMap.subscribe(async (params) => {
      const programName =
        params.get('program') ?? params.get('programName') ?? '';

      this.programName = programName;
      await this.calculateProgramSummary(programName);
    });
  }

  ngOnDestroy() {
    this._filtersSub?.unsubscribe();
    this._displayInRoundedNumberSub?.unsubscribe();
    this._displayedCurrencyCodeSub?.unsubscribe();
    this._currencyUomSub?.unsubscribe();
    this._programsCacheInvalidatedSub?.unsubscribe();
  }

  /**
   * Aggregates TVD program-level metrics and populates component properties for display.
   *
   * Retrieves cached TvdProgramData for the specified program and calculates summary metrics
   * by aggregating across all regions. This method uses program-level data rather than
   * TvdProgramDetailsData because it requires only simple aggregation across regions,
   * whereas details data would require additional complex aggregation logic.
   *
   * @param programName The name of the selected program (from route query parameters)
   */
  private async calculateProgramSummary(programName: string): Promise<void> {
    if (!programName) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    await this._tvdProgramsStore.loadTvdProgramsData();

    this._programTvdData = this._tvdProgramsStore
      .getProgramsDataValue()
      .find((p) => p.program === programName);

    // Calculate summary metrics for the program
    if (this._programTvdData) {
      this.totalValueDelivered =
        this._programTvdData.programMetrics
          ?.map((region) => region.totalValueDelivered.value)
          .reduce((a, b) => a + b, 0) ?? 0;
      this.totalLocations =
        this._programTvdData.programMetrics
          ?.map((region) => region.totalLocations)
          .reduce((a, b) => a + b, 0) ?? 0;
      this.totalProducts =
        this._programTvdData.programMetrics
          ?.map((region) => region.totalProducts)
          .reduce((a, b) => a + b, 0) ?? 0;
      this.impactCategories =
        this._programTvdData.programMetrics?.flatMap((region) =>
          this.parseImpactCategories(region.impactCategories),
        ) ?? [];
      this.averageTvdPerProduct =
        this.totalProducts > 0
          ? this.totalValueDelivered / this.totalProducts
          : 0;
    } else {
      this.totalValueDelivered = 0;
      this.totalLocations = 0;
      this.totalProducts = 0;
      this.impactCategories = [];
      this.averageTvdPerProduct = 0;
    }

    this.isLoading = false;
  }

  private parseImpactCategories(raw: string[] = []): TvdImpactCategory[] {
    return raw
      .map((k) => TvdImpactCategory[k as keyof typeof TvdImpactCategory])
      .filter(Boolean);
  }

  getDisplayValue(value: number): number {
    if (value == null) return 0;

    return Math.round(value);
  }
}
